import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import CoverPreview from '@/components/CoverPreview';

/**
 * GenreAlbumTile — genre "crate" tile on the Collection page.
 *
 * Rendering delegates to <CoverPreview> so the tile stays visually
 * consistent with the CoverPickerModal live preview. When onEditCover
 * is provided, an edit pencil overlays the top-left corner and opens
 * the picker for this crate.
 */
export default function GenreAlbumTile({
  bucket,
  count,
  art = [],
  coverPath = null,
  coverType = 'default_collage',
  coverConfig = {},
  onEditCover,
}) {
  return (
    <div
      data-testid="genre-album-tile"
      className="group relative block rounded-2xl overflow-hidden border border-border bg-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(0,0,0,0.45)]"
    >
      <Link
        to={`/album/${encodeURIComponent(bucket)}`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <div className="transition-transform duration-300 group-hover:scale-[1.03]">
          <CoverPreview
            bucket={bucket}
            art={art}
            coverPath={coverPath}
            coverType={coverType}
            coverConfig={coverConfig}
          />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none">
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="font-display font-semibold text-white text-base sm:text-lg leading-tight">
                {bucket}
              </div>
              <div className="text-[11px] text-white/70 font-mono">
                {count} {count === 1 ? 'pull' : 'pulls'}
              </div>
            </div>
          </div>
        </div>
      </Link>

      {onEditCover && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onEditCover({ bucket, coverPath, coverType, coverConfig, art });
          }}
          className="absolute top-2 left-2 z-10 rounded-full bg-black/70 hover:bg-black/90 text-white/90 hover:text-white p-1.5 backdrop-blur opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
          aria-label={`Change cover for ${bucket}`}
          title="Change cover"
          data-testid={`genre-album-tile-edit-cover-${bucket}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
